import { inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, filter, take, tap, shareReplay } from 'rxjs';
import { KycVerificationUpdateRTM, PaymentProviderAccountApprovalUpdateRTM } from '@gains-com/realtime';
import { environment } from '../../../environments/environment.js';
import { HttpClient } from '@angular/common/http';
import { KycVerification } from '@gains-com/shared';

@Injectable({
  providedIn: 'root',
})
export class KycVerificationViewService {
  private readonly _http = inject(HttpClient);

  private readonly _kycVerification = signal<KycVerificationUpdateRTM | KycVerification | null>(null);

  /** Readonly signal surface for new consumers. */
  readonly kycVerification = this._kycVerification.asReadonly();

  private _fetchKycVerification$ = () => {
    return this._http
      .get<KycVerificationUpdateRTM[] | KycVerification[]>(`${environment.apiUrl}/kyc-verifications`)
      .pipe(
        take(1),
        tap((kycVerification) => {
          this._kycVerification.set(kycVerification?.[0]);
        }),
      );
  };

  kycVerification$: Observable<KycVerificationUpdateRTM | KycVerification> = toObservable(this._kycVerification).pipe(
    tap(() => {
      // If the current value is null, fetch data
      if (this._kycVerification() === null) {
        this._fetchKycVerification$().subscribe();
      }
    }),
    filter((accounts) => accounts !== null),
    shareReplay(1),
  ) as Observable<KycVerificationUpdateRTM | KycVerification>;

  // Method to update payment provider accounts from realtime integration
  updateKycVerification(kycVerification: KycVerificationUpdateRTM | KycVerification) {
    const currentValue = this._kycVerification();

    // If there's no current value, proceed with the update
    if (!currentValue) {
      this._kycVerification.set(kycVerification);
      return;
    }

    // Only update if the new verification was created after the current one
    const currentCreatedAt = new Date(currentValue.created_at);
    const newCreatedAt = new Date(kycVerification.created_at);

    if (newCreatedAt >= currentCreatedAt) {
      this._kycVerification.set(kycVerification);
    }
  }
}
